<template>
  <div v-if="hasCoords" class="">
    <v-card
      variant="elevated"
      :elevation="0"
      class="pa-1 rounded-xs d-inline-flex"
      style="width: fit-content"
    >
      <!-- wrapper that controls stack/inline -->
      <div :class="wrapperClass">
        <!-- WGS84 block -->
        <div class="coord-block d-inline-flex align-center text-no-wrap">
          <span class="text-medium-emphasis">WGS84:</span>
          <b>{{ fmt(lat, latLngDecimals) }}, {{ fmt(lon, latLngDecimals) }}</b>

          <v-btn
            v-if="copy"
            size="x-small"
            variant="text"
            icon="mdi-content-copy"
            @click="copyToClipboard(`${fmt(lat, latLngDecimals)}, ${fmt(lon, latLngDecimals)}`)"
            title="Copy lat,lng"
          />
        </div>

        <!-- divider (only when inline & we have both blocks) -->
        <v-divider v-if="showInlineDivider" vertical class="mx-2 d-none d-md-flex" :inset="false" />

        <!-- Projected block -->
        <div v-if="displayProjected" class="coord-block d-inline-flex align-center text-no-wrap">
          <span class="text-medium-emphasis">{{ targetLabel }}:</span>
          <b>{{ projectedText }}</b>

          <v-btn
            v-if="copy && projectedText"
            size="x-small"
            variant="text"
            icon="mdi-content-copy"
            @click="copyToClipboard(projectedText)"
            title="Copy projected"
          />
        </div>
      </div>
    </v-card>
  </div>

  <div v-else class="text-body-2 text-medium-emphasis">No coordinates</div>
</template>
<script>
import proj4 from "proj4";
import { mapStores } from "pinia";
import { useSettingsStore } from "@/stores/settings";

// OSGB alphanumeric helper (100km squares)
function osgbAlphaGrid(e, n) {
  if (e < 0 || e > 700000 || n < 0 || n > 1300000) {
    return `${Math.round(e)} ${Math.round(n)}`;
  }
  const e100k = Math.floor(e / 100000);
  const n100k = Math.floor(n / 100000);

  const l1 = 19 - n100k - ((19 - n100k) % 5) + Math.floor((e100k + 10) / 5);
  const l2 = (((19 - n100k) * 5) % 25) + (e100k % 5);
  const toLetter = (l) => String.fromCharCode(l + 65 + (l > 7 ? 1 : 0));
  const letters = `${toLetter(l1)}${toLetter(l2)}`;

  const eR = Math.round(e % 100000);
  const nR = Math.round(n % 100000);
  const pad = (num) => num.toString().padStart(5, "0");
  return `${letters} ${pad(eR)} ${pad(nR)}`;
}

export default {
  name: "CoordinateDisplay",
  props: {
    lat: { type: [Number, String], required: false },
    lon: { type: [Number, String], required: false },
    latLngDecimals: { type: Number, default: 6 },
    projectedDecimals: { type: Number, default: 2 },
    copy: { type: Boolean, default: true },
    layout: {
      type: String,
      default: "auto",
      validator: (v) => ["auto", "inline", "stack"].includes(v),
    },
  },
  computed: {
    ...mapStores(useSettingsStore),
    hasCoords() {
      return this.lat != null && this.lon != null && this.lat !== "" && this.lon !== "";
    },
    latNum() {
      return Number(this.lat);
    },
    lonNum() {
      return Number(this.lon);
    },
    wrapperClass() {
      switch (this.layout) {
        case "inline":
          return "d-flex flex-row align-center ga-3";
        case "stack":
          return "d-flex flex-column ga-2";
        default: // auto
          // column on small, row on md+
          return "d-flex flex-column flex-md-row align-md-center ga-2 ga-md-3";
      }
    },
    showInlineDivider() {
      return this.layout !== "stack" && this.displayProjected;
    },
    targetCrs() {
      const crs = this.settingsStore?.crs || {};
      return {
        code: crs.code ?? 4326,
        name: crs.name ?? "WGS84 (Lat/Lon)",
        proj4Def: crs.proj4Def ?? "+proj=longlat +datum=WGS84 +no_defs",
      };
    },

    targetLabel() {
      return this.targetCrs.name;
    },

    displayProjected() {
      // if target is 4326 we could skip, but keep it to still show label if desired
      return this.hasCoords && this.targetCrs?.code != null;
    },

    projectedText() {
      if (!this.displayProjected) return "";
      const epsg = Number(this.targetCrs.code);

      // ensure base
      if (!proj4.defs("EPSG:4326")) {
        proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
      }
      // ensure target
      const key = `EPSG:${epsg}`;
      if (!proj4.defs(key) && this.targetCrs.proj4Def) {
        proj4.defs(key, this.targetCrs.proj4Def);
      }

      try {
        if (epsg === 4326) {
          return `Lat/Lng: ${this.fmt(this.latNum, this.latLngDecimals)}, ${this.fmt(
            this.lonNum,
            this.latLngDecimals
          )}`;
        }
        // proj4 expects [lon, lat]
        const [x, y] = proj4("EPSG:4326", key, [this.lonNum, this.latNum]);

        // Simple per-CRS formatting
        if (epsg === 2157) {
          // Irish ITM
          return `E ${this.fmt(x, this.projectedDecimals)}  N ${this.fmt(
            y,
            this.projectedDecimals
          )}`;
        }
        if (epsg === 27700) {
          // OSGB: show alphanumeric; hover could show numeric if you like
          return osgbAlphaGrid(x, y);
        }
        // generic projected
        return `${this.fmt(x, this.projectedDecimals)}, ${this.fmt(y, this.projectedDecimals)}`;
      } catch {
        return "";
      }
    },
  },
  methods: {
    fmt(n, d = 2) {
      const v = Number(n);
      return Number.isFinite(v) ? v.toFixed(d) : "";
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$toast?.success?.("Copied");
      } catch {
        this.$toast?.info?.(text); // fallback: show text so user can copy
      }
    },
  },
};
</script>
<style scoped>
/* keep body size tweak if you like */
:deep(.text-body-3) {
  font-size: 0.8rem;
}

/* Keep each coordinate pair on one visual chunk */
.coord-block {
  white-space: nowrap;
  font-size: 0.7rem;
}
</style>
